import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import cn from 'classnames';
import get from 'lodash/get';
import { Grid, Divider, Collapse, IconButton } from '@material-ui/core';
import { isEmpty } from 'lodash';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import BurstModeIcon from '@material-ui/icons/BurstMode';
import ClearIcon from '@material-ui/icons/Clear';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { Trans, useTranslation } from '../../../src/i18n';
import { colors, fontSize } from '../../../constants/styleConstants';
import {
  TP,
  EMPTY_INFO_MARK,
  missingItemsLabels,
  PRIVACY_ATTRIBUTE_IDS,
  MS_TO_DAY_DIVIDER,
  PERFORMANCE_TYPES_ID,
} from '../../../constants/consts';
import TitleErrorMessage from '../../TitleErrorMessage';
import { getGroupedProductionsDateForTheCard } from '../../../utils/common';
import DateBlock from '../../ProductionDateBlock';
import ListCard from '../../ListCard';
import EditIcon from '../../Icons/EditIcon';
import TextButton from '../../TextButton';
import DeleteFbIcon from '../../Icons/DeleteFbIcon';
import Button from '../../Button';
import NextLink from '../../NextLink';
import ProductionCommonInfo from './ProductionCommonInfo';
import usePermissions from '../../../utils/permissions';
import DisablingCover from '../../DisablingCover';
import MaskIcon from '../../Icons/MaskIcon';
import StatusLabel from '../../StatusLabel';
import ActionStatusModal from '../../ActionStatusModal';
import { isReqRedMaskShown } from '../../../utils/editCommon';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    backgroundColor: colors.white,
    border: `1px solid ${colors.linesGray}`,
    position: 'relative',
  },
  hiddenProductionRoot: {
    backgroundColor: colors.background.dark,
  },
  draftProductionRoot: {
    backgroundColor: colors.background.biscue,
  },
  infoBlock: {
    padding: theme.spacing(1.5),
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column-reverse',
    },
  },
  actionsBlock: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    flex: 1,
  },
  title: {
    fontSize: fontSize.regularTitle,
  },
  datesContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    marginLeft: theme.spacing(4),
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      marginLeft: 0,
    },
  },
  gridDivider: {
    marginBottom: 8,
  },
  monthBlock: {
    margin: 2,
  },
  errorMessage: {
    width: '100%',
  },
  cardContent: {
    padding: theme.spacing(1.5),
  },
  editButton: {
    marginLeft: 'auto',
  },
  expand: {
    padding: 2,
    cursor: 'pointer',
    transform: 'rotate(0deg)',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
    display: 'inline-block',
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
  moreIcon: {
    color: colors.blue,
  },
  publishButton: {
    padding: 4,
    minWidth: 0,
  },
  productionInfoBlock: {
    alignItems: 'flex-start',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  productionInfo: {
    flex: 1,
  },
  redMaskIcon: {
    position: 'relative',
    top: 2,
  },
  actionLink: {
    marginTop: 2,
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(),
    '&>*:not(:last-child)': {
      marginRight: theme.spacing(2),
    },
  },
}));

const EditProductionCardLayout = ({
  className,
  production,
  datesBlock,
  infoBlock,
  onDeleteClick,
  onPublishClick,
  onCancelClick,
  editLinkProps,
  mediaLinkProps,
  setVisibility,
  profile,
  removeProduction,
  isLoading,
  requestRedMaskLink,
}) => {
  const DELETION_DAYS_LIMIT = 7;
  const classes = useStyles();
  const { t } = useTranslation();
  const { isAdmin } = usePermissions();
  // eslint-disable-next-line camelcase
  const isPublished = Boolean(production?.published_at);
  const [expanded, setExpanded] = React.useState(false);
  const [rejectEditModalOpened, setRejectEditModalOpened] = React.useState(false);
  const [isHidden, setVisibilityState] = React.useState(
    production.privacy.find(el => el.profile?.id === profile?.id)?.privacyAttribute?.id ===
      PRIVACY_ATTRIBUTE_IDS.PRIVATE,
  );

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const prodDates = React.useCallback(
    () =>
      getGroupedProductionsDateForTheCard(production, false, date =>
        get(production, 'selectedDates', []).includes(date?.date),
      ),
    [production],
  );

  const switchVisibility = () => {
    setVisibility(!isHidden ? PRIVACY_ATTRIBUTE_IDS.PRIVATE : PRIVACY_ATTRIBUTE_IDS.PUBLIC, production);
    setVisibilityState(!isHidden);
  };

  const isDeleteDisplayed = () => {
    const today = +new Date();
    const publishDate = +new Date(production.published_at);
    const numOfDaysAfterPublishing = (today - publishDate) / MS_TO_DAY_DIVIDER;
    return numOfDaysAfterPublishing < DELETION_DAYS_LIMIT;
  };

  const notCancelled = () => !production?.performanceTypes?.find(el => el?.id === PERFORMANCE_TYPES_ID.CANCELLED);

  return (
    <>
      <ListCard
        className={cn(classes.root, className, {
          [classes.hiddenProductionRoot]: isHidden,
          [classes.draftProductionRoot]: !isPublished,
        })}
      >
        {isLoading && <DisablingCover />}
        <Grid container className={classes.infoBlock} spacing={2}>
          <Grid item container spacing={2} className={classes.actionsBlock} alignItems="flex-start">
            <Grid item container spacing={2} alignItems="flex-start" className={classes.productionInfoBlock}>
              <Grid item>
                <IconButton
                  onClick={handleExpandClick}
                  aria-expanded={expanded}
                  aria-label="show more"
                  className={cn(classes.expand, {
                    [classes.expandOpen]: expanded,
                  })}
                >
                  <ExpandMoreIcon className={classes.moreIcon} />
                </IconButton>
                <StatusLabel status={isPublished ? 'green' : undefined}>
                  {t(`${TP}.${isPublished ? 'FN_EDITSEASON_PUBLISHED_STATUS' : 'FN_EDITSEASON_UNPUBLISHED_STATUS'}`)}
                </StatusLabel>
              </Grid>
              <Grid item className={classes.productionInfo}>
                <ProductionCommonInfo production={production} profile={profile} />
              </Grid>
            </Grid>
            {isEmpty(prodDates()) ? (
              EMPTY_INFO_MARK
            ) : (
              <Grid sm={12} xs={12} item className={classes.datesContainer}>
                {Object.keys(prodDates()).map((key, index) =>
                  React.cloneElement(datesBlock, {
                    key: `${key}-${index}`,
                    title: key,
                    dates: get(prodDates(), key, []),
                    className: classes.monthBlock,
                    onDateClick: () => {},
                  }),
                )}
              </Grid>
            )}
          </Grid>
          <Grid item className={classes.editButton}>
            {editLinkProps ? (
              <NextLink href={editLinkProps} startIcon={<EditIcon />} type="text">
                {t(`${TP}.m_EDIT`)}
              </NextLink>
            ) : (
              <TextButton startIcon={<EditIcon />} type="text" onClick={() => setRejectEditModalOpened(true)}>
                {t(`${TP}.m_EDIT`)}
              </TextButton>
            )}
          </Grid>
        </Grid>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Divider className={classes.gridDivider} />
          <div className={classes.cardContent}>
            <div className={classes.buttons}>
              {mediaLinkProps && (
                <NextLink href={mediaLinkProps} startIcon={<BurstModeIcon />}>
                  {t(`${TP}.FN_MEDIA`)}
                </NextLink>
              )}
              {requestRedMaskLink && isReqRedMaskShown(production) && (
                <NextLink
                  className={classes.actionLink}
                  href={requestRedMaskLink}
                  startIcon={<MaskIcon className={classes.redMaskIcon} isRed />}
                >
                  {t(`${TP}.FN_REQUEST_RED_MASK`)}
                  {Boolean(production?.validationStatus) && `: ${production?.validationStatus?.name}`}
                </NextLink>
              )}
              {profile && isPublished && (
                <TextButton
                  startIcon={isHidden ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  onClick={switchVisibility}
                >
                  {t(isHidden ? `${TP}.FN_UNHIDE` : `${TP}.FN_HIDE`)}
                </TextButton>
              )}
              {isPublished && onCancelClick && notCancelled() && (
                <TextButton startIcon={<ClearIcon />} color="default" onClick={onCancelClick}>
                  {t(`${TP}.FN_EDITSEASON_FULLPROD_MARK_AS_CANCELED`)}
                </TextButton>
              )}
              {!isPublished && onPublishClick && (
                <Button variant="outlined" onClick={onPublishClick} className={classes.publishButton}>
                  {t(`${TP}.FN_PUBLISH`)}
                </Button>
              )}
              {removeProduction && (
                <TextButton startIcon={<DeleteFbIcon color="gray" />} onClick={() => removeProduction(production)}>
                  {t(`${TP}.FN_REMOVE`)}
                </TextButton>
              )}
              {onDeleteClick && (isAdmin || !isPublished || isDeleteDisplayed()) && (
                <TextButton startIcon={<DeleteFbIcon />} color="default" onClick={onDeleteClick}>
                  {t(`${TP}.m_DELETE`)}
                </TextButton>
              )}
            </div>
            {production?.missing?.length > 0 && (
              <TitleErrorMessage className={classes.errorMessage}>
                <span className={classes.title}>{`${t(`${TP}.m_MISSING`)} ${production?.missing
                  .map(item => t(`${TP}.${missingItemsLabels[item?.id]}`))
                  .join(', ')}`}</span>
              </TitleErrorMessage>
            )}
            {infoBlock && <div className={classes.infoBlock}>{infoBlock}</div>}
          </div>
        </Collapse>
      </ListCard>
      {rejectEditModalOpened && (
        <ActionStatusModal
          isOpened
          onClose={() => setRejectEditModalOpened(false)}
          type="info"
          buttons={[<Button onClick={() => setRejectEditModalOpened(false)}>{t(`${TP}.FN_CLOSE`)}</Button>]}
        >
          <Trans
            i18nKey={`${TP}.FN_REJECT_EDIT_PRODUCTION_INFO`}
            components={{ emaillink: <a href="mailto:contact@operabase.com" /> }}
          />
        </ActionStatusModal>
      )}
    </>
  );
};

EditProductionCardLayout.propTypes = {
  className: PropTypes.string,
  production: PropTypes.object.isRequired,
  datesBlock: PropTypes.element,
  infoBlock: PropTypes.element,
  onDeleteClick: PropTypes.func,
  onPublishClick: PropTypes.func,
  onCancelClick: PropTypes.func,
  editLinkProps: PropTypes.object.isRequired,
  mediaLinkProps: PropTypes.object,
  setVisibility: PropTypes.func,
  profile: PropTypes.object,
  removeProduction: PropTypes.func,
  isLoading: PropTypes.bool,
  requestRedMaskLink: PropTypes.object,
};

EditProductionCardLayout.defaultProps = {
  className: '',
  datesBlock: <DateBlock />,
  mediaLinkProps: null,
  setVisibility: () => {},
  profile: null,
  removeProduction: undefined,
  isLoading: false,
  requestRedMaskLink: null,
};

export default memo(EditProductionCardLayout);
